// The portada is the first paint an anonymous visitor gets, so it is imported
// eagerly — and by subpath, not through the barrel, so it brings only itself
// rather than the whole platform skin. Every other screen is lazy below.
import {
  LEGACY_REDIRECTS,
  routes as pencilRoutes,
} from "@orkestor/skin-pencil-chief/routes";
import { ADMIN_BASE, routes as adminRoutes } from "@orkestor/skin-admin/routes";
import { routes as checkmeRoutes } from "@orkestor/skin-check-me/routes";
import { Portada } from "@orkestor/skin-platform/screens/Portada";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useActiveOrg } from "@orkestor/core";

import { HOME_PATH } from "./paths";
import { ProductoActivo } from "./ProductoActivo";
import { productCatalogue, SHORTCUTS } from "./products";
import { RouteError } from "./RouteError";
import { Shell } from "./Shell";

/** Routes. `/` is the public portada; everything else lives under the
    authenticated shell. Screens without backend yet render <PendingBackend>.

    Skins are loaded per route with `lazy`, so the shell never ships a product
    nobody opened (`frontend-standards.md` §Performance). Each `import()` below
    resolves to one chunk per skin: someone who only visits the organization
    console never downloads Pencil Chief's console, and vice versa. */

const platform = () => import("@orkestor/skin-platform");
const admin = () => import("@orkestor/skin-admin");
const pencil = () => import("@orkestor/skin-pencil-chief");
const checkme = () => import("@orkestor/skin-check-me");

/** A route that renders one screen of a skin, by export name. */
function screen<M extends Record<string, unknown>>(
  load: () => Promise<M>,
  name: keyof M & string,
) {
  return async () => ({
    Component: (await load())[name] as React.ComponentType,
  });
}

/** The organization console is one component driven by a section prop. */
function orgSection(
  section: "perfil" | "miembros" | "productos" | "reputacion" | "facturacion",
) {
  return async () => {
    const { OrganizacionConsole } = await platform();
    return { Component: () => <OrganizacionConsole section={section} /> };
  };
}

/** The home needs Pencil's counters, so it loads that one module by subpath —
    not the barrel, which would drag every Pencil screen into the landing page
    for four numbers. */
const inicio = async () => {
  const [{ Inicio }, { PencilStats }] = await Promise.all([
    platform(),
    import("@orkestor/skin-pencil-chief/components/PencilStats"),
  ]);
  return {
    Component: () => {
      // La organización activa decide qué productos se ofrecen como abiertos.
      // Sin esto, la tarjeta decía «Activo» —cierto: el producto está lanzado—
      // y quien lo leía entendía «lo tengo»: pulsaba «Abrir», la puerta de rutas
      // le devolvía aquí, y desde fuera se ve igual que un botón roto.
      const { activeOrg } = useActiveOrg();
      return (
        <Inicio
          products={productCatalogue(<PencilStats />, activeOrg?.products ?? undefined)}
          shortcuts={SHORTCUTS}
        />
      );
    },
  };
};

/** The gate, as a LAYOUT route around the whole area.
 *
 *  Not per screen. Wrapping each screen meant the next slice had to REMEMBER to
 *  wrap its section, and forgetting is silent: the rail hides itself, but the
 *  screen renders for anyone who types the URL — the first review's Blocker,
 *  re-openable once per section, with every test staying green. As a layout route
 *  the gate is structural: a child cannot be added outside it. */
const adminGate = async () => {
  const { SoloSysadmin } = await admin();
  return {
    Component: () => (
      <SoloSysadmin homePath={HOME_PATH}>
        <Outlet />
      </SoloSysadmin>
    ),
  };
};

/** The back office's front page. The status sentence comes from the platform
    skin, and the shell composes it in — the same move as `PencilStats` on the
    home, and for the same reason: a skin may compose only the layers below it,
    so skin-admin must not import from skin-platform. The shell is the one place
    allowed to know both. */
/** Productoras: el listado global. Necesita saber si quien mira es sysadmin para
    decidir si OFRECE borrar — el servidor lo exige igual, esto sólo evita enseñar
    una puerta que no se va a abrir. El gate de la sección ya garantiza que hasta
    aquí sólo llega un sysadmin; el dato se pasa igualmente porque el día que la
    sección se abra a staff, la pantalla ya sabe distinguir. */
/** Técnicos: la búsqueda vive en la URL, como en productoras, para que abrir una
    ficha y volver la conserve. */
const adminTecnicos = async () => {
  const { Tecnicos } = await admin();
  return {
    Component: () => {
      const [params, setParams] = useSearchParams();
      const navigate = useNavigate();
      return (
        <Tecnicos
          busqueda={params.get("q") ?? ""}
          onBuscar={(q) => setParams(q ? { q } : {}, { replace: true })}
          onAbrir={(userId) => navigate(adminRoutes.tecnico(userId))}
        />
      );
    },
  };
};

const adminTecnico = async () => {
  const { Tecnico } = await admin();
  return {
    Component: () => {
      const { userId } = useParams();
      const navigate = useNavigate();
      return (
        <Tecnico
          userId={userId!}
          onVolver={() =>
            window.history.state?.idx > 0
              ? navigate(-1)
              : navigate(adminRoutes.tecnicos)
          }
        />
      );
    },
  };
};

const adminProductoras = async () => {
  const { Productoras } = await admin();
  return {
    Component: () => {
      // La búsqueda vive en la URL, y por eso abrir una ficha y volver la
      // conserva: el estado no está en un `useState` que se desmonta con la
      // pantalla. Quien conoce la URL es el shell — una skin sólo compone las
      // capas de debajo, y react-router no es una de ellas.
      const [params, setParams] = useSearchParams();
      const navigate = useNavigate();
      return (
        <Productoras
          esSysadmin
          busqueda={params.get("q") ?? ""}
          onBuscar={(q) => setParams(q ? { q } : {}, { replace: true })}
          onAbrir={(id) => navigate(adminRoutes.productora(id))}
        />
      );
    },
  };
};

/** Monitorización: el marco es de la trastienda y la rejilla escrita viene del
    skin de plataforma. Igual que el Resumen y su frase de estado, y por lo mismo:
    un skin sólo compone las capas de debajo, así que skin-admin no puede importar
    de skin-platform. El shell es el único que conoce a los dos. */
const adminProductos = async () => {
  const { Productos } = await admin();
  return { Component: Productos };
};

const adminConfiguracion = async () => {
  const { Configuracion } = await admin();
  return { Component: Configuracion };
};

const adminMonitorizacion = async () => {
  const [{ Monitorizacion }, { DetalleSalud }] = await Promise.all([
    admin(),
    import("@orkestor/skin-platform/components/DetalleSalud"),
  ]);
  return { Component: () => <Monitorizacion detalle={<DetalleSalud />} /> };
};

/** La ficha de una productora. El id sale del router y el «volver» lo pone el
    shell, para que la pantalla no dependa de react-router — la misma regla por la
    que el Resumen recibe su frase de estado en vez de importarla. */
const adminProductora = async () => {
  const { Productora } = await admin();
  return {
    Component: () => {
      const { orgId } = useParams();
      const navigate = useNavigate();
      return (
        <Productora
          organizationId={orgId!}
          // `-1` vuelve a la URL exacta de la que viniste, con su `?q=` puesto
          // — navegar a la ruta pelada perdería la búsqueda, que es justo lo que
          // subirla a la URL vino a evitar.
          //
          // Pero sólo si HAY de dónde volver. Con un enlace directo, un marcador
          // o una pestaña nueva no hay entrada previa y `-1` te saca de la
          // aplicación, bajo un botón que dice «Todas las productoras». Así que
          // se mira el historial: si esta es la primera parada, se va al listado.
          onVolver={() =>
            window.history.state?.idx > 0
              ? navigate(-1)
              : navigate(adminRoutes.productoras)
          }
        />
      );
    },
  };
};

const adminResumen = async () => {
  const [{ Resumen }, { ResumenEstado }] = await Promise.all([
    admin(),
    import("@orkestor/skin-platform/components/ResumenEstado"),
  ]);
  return {
    Component: () => (
      <Resumen
        estado={
          <span className="flex items-baseline gap-2">
            <ResumenEstado />
            {/* La frase resume; el detalle está en Monitorización. Desde que la
                rejilla se mudó del rail ésta es la única entrada a los semáforos
                desde el Resumen, y una frase sin puerta obliga a saberse el rail
                de memoria.

                El enlace lo pone el SHELL y no la pantalla: `Resumen` no depende
                del router a propósito —recibe su frase como nodo—, y meterle un
                `Link` le añadía esa dependencia y rompía sus 45 tests, que es la
                forma que tiene un contrato de avisar de que lo estás cambiando. */}
            <Link
              to={adminRoutes.monitorizacion}
              className="text-[12px] text-dim underline"
            >
              Ver detalle
            </Link>
          </span>
        }
      />
    ),
  };
};

export const router = createBrowserRouter([
  { path: "/", element: <Portada homePath={HOME_PATH} /> },
  // The design-system catalogue, rendering itself. Lazy and outside the shell:
  // it needs no session, and it must cost nothing in a normal page load.
  {
    path: "/design",
    lazy: async () => ({
      Component: (await import("@orkestor/design-system/Gallery")).Gallery,
    }),
  },
  // NOTE: /acceso/callback and /acceso/silent are handled in main.tsx BEFORE the
  // providers mount (see the comment there), so they are intentionally not routes.
  {
    element: <Shell />,
    // If the shell itself (session/providers) fails, show the branded error too.
    errorElement: <RouteError />,
    children: [
      // A pathless layout route whose only job is to catch a screen's error and
      // render it INSIDE the shell's outlet (chrome preserved) instead of
      // react-router's raw overlay. A route with no element renders <Outlet/>.
      {
        errorElement: <RouteError />,
        children: [
          // The ecosystem home: the shell's own screen, before any product.
          { path: HOME_PATH, lazy: inicio },
          // Pencil Chief, namespaced so a second product needs no rename.
          // Pencil's own panel (frame 03): the five engagement tiles + open offers +
          // upcoming shoots, from the aggregate `/engagement-stats` (OEM-273).
          { path: pencilRoutes.panel, lazy: screen(pencil, "Panel") },
          { path: pencilRoutes.ofertas, lazy: screen(pencil, "Ofertas") },
          {
            path: pencilRoutes.ofertaNueva,
            lazy: screen(pencil, "PublicarOferta"),
          },
          {
            path: `${pencilRoutes.ofertas}/:jobId/editar`,
            lazy: screen(pencil, "PublicarOferta"),
          },
          {
            path: `${pencilRoutes.ofertas}/:jobId`,
            lazy: screen(pencil, "GestionOferta"),
          },
          { path: pencilRoutes.buscar, lazy: screen(pencil, "BuscarTecnicos") },
          // The producer's view of one technician (mockup screen 11): profile,
          // reputation and the private cancel rastro (OEM-270 / OEM-192).
          {
            path: `${pencilRoutes.base}/tecnicos/:profileId`,
            lazy: screen(pencil, "PerfilTecnico"),
          },
          // Reseñas (OEM-197 dep): mutual, blind reviews — write what you owe and see
          // your production's rating.
          { path: pencilRoutes.resenas, lazy: screen(pencil, "Resenas") },
          { path: pencilRoutes.mensajes, lazy: screen(pencil, "Mensajes") },
          {
            path: pencilRoutes.agenda,
            lazy: screen(pencil, "AgendaProduccion"),
          },
          // Links shared while the app served flat paths keep working.
          ...LEGACY_REDIRECTS.map(([from, to]) => ({
            path: from,
            element: <Navigate to={to} replace />,
          })),
          // Check Me, tras su puerta de activación: una productora que no lo tiene
          // contratado no descarga su bundle ni ve una pantalla a medias — vuelve al
          // inicio. La puerta va en una ruta envolvente y no dentro de cada pantalla
          // por lo mismo que `role_of` decide el permiso en el read model: repartir
          // el `if` por cinco pantallas son cinco ocasiones de olvidarlo, y la sexta
          // nacería ya rota.
          {
            element: <ProductoActivo producto="check_me" />,
            children: [
              {
                // La envoltura de la skin, que es la que lleva su paleta. Sin ella
                // Check Me se pintaría de negro y amarillo — compilando igual.
                lazy: async () => ({
                  Component: (await checkme()).ConsolaCheckMe,
                }),
                children: [
                  // «Hoy» ENCUENTRA la jornada; el panel espera un id en la URL.
                  // Apuntar esta ruta al panel enseñaba «no se ha podido abrir esta
                  // jornada» a todo el mundo, siempre.
                  { path: checkmeRoutes.hoy, lazy: screen(checkme, "Hoy") },
                  {
                    path: `${checkmeRoutes.base}/jornadas/:shiftId`,
                    lazy: screen(checkme, "Jornada"),
                  },
                  {
                    path: `${checkmeRoutes.base}/jornadas/:shiftId/parte`,
                    lazy: screen(checkme, "ParteDiario"),
                  },
                  {
                    path: `${checkmeRoutes.base}/producciones/:productionId/personas/:userId`,
                    lazy: screen(checkme, "Persona"),
                  },
                  {
                    path: `${checkmeRoutes.base}/producciones/:productionId/convenio`,
                    lazy: screen(checkme, "Convenio"),
                  },
                  {
                    path: `${checkmeRoutes.base}/producciones/:productionId/informes`,
                    lazy: screen(checkme, "Informes"),
                  },
                  {
                    // El equipo. `routes.equipo` existía desde el primer día y
                    // no apuntaba a nada: sin esta pantalla nadie entra en un
                    // equipo, y sin equipo nadie ficha.
                    path: `${checkmeRoutes.base}/producciones/:productionId/equipo`,
                    lazy: screen(checkme, "Equipo"),
                  },
                ],
              },
            ],
          },
          { path: "/organizacion", lazy: screen(platform, "Organizacion") },
          {
            path: "/organizacion/:orgId",
            element: <Navigate to="perfil" replace />,
          },
          { path: "/organizacion/:orgId/perfil", lazy: orgSection("perfil") },
          {
            path: "/organizacion/:orgId/miembros",
            lazy: orgSection("miembros"),
          },
          {
            path: "/organizacion/:orgId/productos",
            lazy: orgSection("productos"),
          },
          {
            path: "/organizacion/:orgId/reputacion",
            lazy: orgSection("reputacion"),
          },
          {
            path: "/organizacion/:orgId/facturacion",
            lazy: orgSection("facturacion"),
          },
          {
            path: "/invitaciones/aceptar/:token",
            lazy: screen(platform, "AceptarInvitacion"),
          },
          { path: "/bloqueos", lazy: screen(platform, "Bloqueos") },
          // Administración (admin-area): the back office. Its own area, reached from
          // the account menu, deliberately absent from the product switcher.
          //
          // Inside the pathless error-boundary route (OEM-351), like every other
          // screen: a failure in the back office should render the branded error in
          // the shell's outlet, not react-router's raw overlay.
          //
          // The path comes from the skin, never a literal: the rail and the account
          // menu both derive theirs from it, and a router hardcoding "/admin" would
          // let the three drift apart silently.
          // Everything under the area sits inside the gate. Sections added by later
          // slices go in `children` and are gated by construction.
          {
            path: ADMIN_BASE,
            lazy: adminGate,
            children: [
              { index: true, lazy: adminResumen },
              { path: "monitorizacion", lazy: adminMonitorizacion },
              { path: "configuracion", lazy: adminConfiguracion },
              { path: "productos", lazy: adminProductos },
              { path: "tecnicos", lazy: adminTecnicos },
              { path: "tecnicos/:userId", lazy: adminTecnico },
              { path: "productoras", lazy: adminProductoras },
              { path: "productoras/:orgId", lazy: adminProductora },
              // The area's other sections are listed in the rail as unavailable until
              // their slice lands. Typing one of their paths returns to the dashboard
              // rather than to a blank screen — an unbuilt section must not be
              // reachable by URL either.
              { path: "*", element: <Navigate to={ADMIN_BASE} replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
